// afiliasi.view — tab 🤝 Afiliasi di menu CRM.
//
// Satu halaman untuk melihat: poin servis, poin afiliasi, dan totalnya; siapa mengajak siapa;
// servis mana yang mencairkan bonus; dan kirim ajakan jadi perekomendasi.
import { esc, wa62, fillTpl } from '@domain/format';
import { waTpl } from '@core/settings';
import { openModal } from '@ui/modal';
import { toast } from '@ui/toast';
import { openWA } from '@core/wa';
import { searchBoxHTML, wireSearchBox } from '@ui/searchbox';
import * as repo from './crm.repo';
import * as afRepo from './afiliasi.repo';
import * as loyaltyRepo from './loyalty.repo';
import { afiliasiCfg } from './afiliasi.service';
import * as svc from './crm.service';

interface BarisAf {
  id: number; nama: string; wa: string | null;
  poinServis: number; poinAfiliasi: number; total: number;
  jmlAjak: number; bonusAktif: number; bonusDitarik: number;
}

let afQ = '';
let afHanyaAjak = true;   // biasanya yang dicari adalah yang SUDAH pernah mengajak

export async function renderAfiliasiTab(host: HTMLElement): Promise<void> {
  host.innerHTML = '<div class="panel"><div class="empty">Memuat…</div></div>';
  const cfg = afiliasiCfg();

  let custs: repo.CrmCustomer[], spend: Map<number, repo.Spend>, redeemed: Map<number, number>;
  let grants: Map<number, afRepo.Grant[]>, refs: { id: number; nama: string; referrer_id: number | null }[];
  try {
    [custs, spend, redeemed, grants, refs] = await Promise.all([
      repo.listCustomers(), repo.spendByCustomer(), repo.redeemedByCustomer(),
      afRepo.grantsAktifSemua(), afRepo.daftarRekomendasi(),
    ]);
  } catch (e) { host.innerHTML = `<div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const nama = new Map(custs.map((c) => [c.id, c.nama]));
  const jmlAjak = new Map<number, number>();
  for (const r of refs) if (r.referrer_id) jmlAjak.set(r.referrer_id, (jmlAjak.get(r.referrer_id) ?? 0) + 1);

  const rows: BarisAf[] = custs.map((c) => {
    const s = spend.get(c.id) ?? { ltv: 0, trx: 0, last: null, per: [] };
    const g = (grants.get(c.id) ?? []).map((x) => ({ id: x.id, tgl: x.tgl, poin: x.poin, alasan: x.alasan, ditarik: !!x.ditarik_at, berlakuBulan: x.berlaku_bulan }));
    const pecah = svc.saldoPerSumber(s.per, redeemed.get(c.id) ?? 0, g);
    return {
      id: c.id, nama: c.nama, wa: c.wa,
      poinServis: pecah.servis, poinAfiliasi: pecah.afiliasi, total: pecah.total,
      jmlAjak: jmlAjak.get(c.id) ?? 0,
      bonusAktif: g.filter((x) => !x.ditarik).reduce((a, x) => a + x.poin, 0),
      bonusDitarik: 0,
    };
  });

  const q = afQ.trim().toLowerCase();
  const shown = rows
    .filter((r) => (!afHanyaAjak || r.jmlAjak > 0 || r.poinAfiliasi > 0))
    .filter((r) => !q || `${r.nama} ${r.wa ?? ''}`.toLowerCase().includes(q))
    .sort((a, b) => b.jmlAjak - a.jmlAjak || b.poinAfiliasi - a.poinAfiliasi);

  const totalAjak = refs.filter((r) => r.referrer_id).length;
  const totalBonus = rows.reduce((a, r) => a + r.bonusAktif, 0);
  const withWa = shown.filter((r) => wa62(r.wa ?? ''));

  const body = shown.map((r) => `<tr>
      <td><b>${esc(r.nama)}</b>${r.jmlAjak ? ` <span class="pill" style="background:var(--okbg);color:var(--ok)">${r.jmlAjak} diajak</span>` : ''}</td>
      <td class="r">${r.poinServis}</td>
      <td class="r" style="color:var(--ok)">${r.poinAfiliasi}</td>
      <td class="r"><b>${r.total}</b></td>
      <td style="white-space:nowrap">
        ${r.jmlAjak || r.poinAfiliasi ? `<button class="btn" style="padding:3px 8px" data-afhist="${r.id}" data-n="${esc(r.nama)}" title="Riwayat rekomendasi">🧾</button>` : ''}
        ${wa62(r.wa ?? '') ? ` <button class="btn" style="padding:3px 8px" data-afajak="${r.id}" data-w="${esc(wa62(r.wa ?? ''))}" data-n="${esc(r.nama)}" data-p="${r.total}" title="Kirim ajakan jadi perekomendasi">💬</button>` : ''}
      </td></tr>`).join('')
    || `<tr><td colspan="5" class="empty">${q ? 'Tak ada yang cocok.' : (afHanyaAjak ? 'Belum ada yang pernah merekomendasikan. Hilangkan centang untuk melihat semua pelanggan.' : 'Belum ada pelanggan.')}</td></tr>`;

  host.innerHTML = `
    <div class="scope-note">${cfg.aktif
      ? `Program <b>aktif</b> — <b>${cfg.bonus} poin</b> per rekomendasi yang jadi${cfg.batasBulan ? `, maksimal <b>${cfg.batasBulan}</b>/bulan per orang` : ''}. Bonus cair setelah servis pertama teman yang diajak <b>lunas &amp; diverifikasi</b>.`
      : 'Program afiliasi sedang <b>nonaktif</b> — aktifkan di Pengaturan → 🎁 Poin Loyalty → 🤝 Program Afiliasi.'}</div>
    <div class="stat-grid" style="margin-bottom:10px">
      <div class="stat"><div class="lbl">Pelanggan dari rekomendasi</div><div class="num">${totalAjak}</div></div>
      <div class="stat"><div class="lbl">Poin bonus beredar</div><div class="num" style="color:var(--ok)">${totalBonus}</div></div>
      <div class="stat"><div class="lbl">Perekomendasi aktif</div><div class="num">${rows.filter((r) => r.jmlAjak > 0).length}</div></div>
    </div>
    <div class="toolbar" style="display:flex;gap:8px;align-items:center;margin:10px 0;flex-wrap:wrap">
      ${searchBoxHTML('af_cari', afQ, '🔎 Cari nama / WA…', 'Cari perekomendasi')}
      <label style="display:flex;align-items:center;gap:5px;font-size:12.5px"><input type="checkbox" id="af_hanya" ${afHanyaAjak ? 'checked' : ''}> Hanya yang pernah mengajak</label>
      ${withWa.length ? `<button class="btn primary" id="af_bc" style="margin-left:auto">📣 Ajak jadi perekomendasi (${withWa.length})</button>` : ''}
    </div>
    <table class="data"><thead><tr>
      <th>Pelanggan</th><th class="r">Poin servis</th><th class="r">Poin afiliasi</th><th class="r">Total</th><th>Aksi</th>
    </tr></thead><tbody>${body}</tbody></table>`;

  const ulang = (): void => { void renderAfiliasiTab(host); };
  wireSearchBox(host, 'af_cari', (v) => { afQ = v; }, ulang);
  host.querySelector('#af_hanya')?.addEventListener('change', (e) => { afHanyaAjak = (e.target as HTMLInputElement).checked; ulang(); });
  host.querySelectorAll<HTMLElement>('button[data-afhist]').forEach((b) => b.addEventListener('click', () =>
    void riwayatAfiliasi(+(b.dataset['afhist'] ?? 0), b.dataset['n'] ?? '', nama)));
  host.querySelectorAll<HTMLElement>('button[data-afajak]').forEach((b) => b.addEventListener('click', () =>
    void kirimAjakan(+(b.dataset['afajak'] ?? 0), b.dataset['w'] ?? '', b.dataset['n'] ?? '', +(b.dataset['p'] ?? 0), cfg.bonus)));
  host.querySelector('#af_bc')?.addEventListener('click', () => ajakanMassal(withWa, cfg.bonus));
}

/** Riwayat: siapa yang direkomendasikan, servis mana, cair/ditarik kapan & kenapa. */
async function riwayatAfiliasi(id: number, namaOrang: string, nama: Map<number, string>): Promise<void> {
  let g: afRepo.Grant[];
  try { g = await afRepo.grantsFor(id); } catch (e) { toast((e as Error).message); return; }
  const baris = g.map((x) => `<div class="hist" style="border-left:3px solid ${x.ditarik_at ? 'var(--danger)' : 'var(--ok)'}">
      <div class="h1"><span><b>${x.ditarik_at ? '−' : '+'}${x.poin} poin</b> · ${esc(x.referred_id ? (nama.get(x.referred_id) ?? 'pelanggan') : '—')}</span>
        <span class="outlet-tag">${esc(x.tgl)}</span></div>
      <div class="h2">${x.ticket_no ? `servis <b>${esc(x.ticket_no)}</b> · ` : ''}${esc(x.alasan)}
        ${x.ditarik_at ? `<br><span style="color:var(--danger)">↩️ ditarik: ${esc(x.ditarik_alasan ?? '')} (oleh ${esc(x.ditarik_by ?? '—')})</span>` : ''}</div>
    </div>`).join('') || '<div class="empty">Belum ada bonus rekomendasi.</div>';
  const aktif = g.filter((x) => !x.ditarik_at).reduce((a, x) => a + x.poin, 0);
  const ditarik = g.filter((x) => x.ditarik_at).reduce((a, x) => a + x.poin, 0);
  const bg = openModal(`<div class="modal-head"><h3>🤝 Riwayat Rekomendasi — ${esc(namaOrang)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="stat-grid" style="margin-bottom:10px">
        <div class="stat"><div class="lbl">Bonus aktif</div><div class="num" style="color:var(--ok)">${aktif}</div></div>
        <div class="stat"><div class="lbl">Ditarik</div><div class="num" style="color:var(--danger)">${ditarik}</div></div>
      </div>${baris}</div>
    <div class="modal-foot"><button class="btn primary" id="af_tutup">Tutup</button></div>`);
  bg.querySelector('#af_tutup')?.addEventListener('click', () => bg.remove());
}

async function kirimAjakan(id: number, wa: string, namaOrang: string, poin: number, bonus: number): Promise<void> {
  let link = '';
  try { link = `${location.origin}/poin.html#t=${await loyaltyRepo.tokenPoin(id)}`; } catch { /* tautan opsional */ }
  openWA(wa, fillTpl(waTpl().ajakAfiliasi, { nama: namaOrang, bonus: String(bonus), poin: String(poin), link }));
}

function ajakanMassal(list: BarisAf[], bonus: number): void {
  const bg = openModal(`<div class="modal-head"><h3>📣 Ajak jadi perekomendasi — ${list.length} pelanggan</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Klik 💬 per pelanggan — pesannya sudah terisi nama, besaran bonus, poin, dan tautan poin masing-masing.</div>
      <div class="field"><label>Pesan</label><textarea id="aj_msg" rows="7">${esc(waTpl().ajakAfiliasi)}</textarea>
        <div class="hint">Isian otomatis: <b>{nama}</b> · <b>{bonus}</b> poin per rekomendasi · <b>{poin}</b> poin sekarang · <b>{link}</b> tautan poin</div></div>
      <div style="max-height:38vh;overflow:auto">${list.map((r) => `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid var(--line);font-size:13px">
        <span>${esc(r.nama)} · ${esc(wa62(r.wa ?? ''))}<br><span style="color:var(--muted);font-size:11.5px">${r.total} poin${r.jmlAjak ? ` · sudah mengajak ${r.jmlAjak}` : ''}</span></span>
        <button class="btn" style="padding:2px 8px;flex:none" data-aj="${r.id}" data-w="${esc(wa62(r.wa ?? ''))}" data-n="${esc(r.nama)}" data-p="${r.total}">💬</button></div>`).join('')}</div></div>
    <div class="modal-foot"><button class="btn" id="aj_tutup">Tutup</button></div>`);
  const msg = (): string => (bg.querySelector('#aj_msg') as HTMLTextAreaElement).value;
  bg.querySelector('#aj_tutup')?.addEventListener('click', () => bg.remove());
  bg.querySelectorAll<HTMLElement>('button[data-aj]').forEach((b) => b.addEventListener('click', async () => {
    let link = '';
    try { link = `${location.origin}/poin.html#t=${await loyaltyRepo.tokenPoin(+(b.dataset['aj'] ?? 0))}`; } catch { /* opsional */ }
    openWA(b.dataset['w'], fillTpl(msg(), { nama: b.dataset['n'] ?? '', bonus: String(bonus), poin: b.dataset['p'] ?? '0', link }));
  }));
}
